<template>
  <div class="codewrap">
    <div class="code">
      <pre
        v-if="
          !viewSMVMode &&
          !videoThumbnailStatus &&
          !spinControlPanelStatus &&
          !modelThumbnailStatus &&
          !modelSkyboxStatus &&
          !modelEnvironmentStatus &&
          !modelSkyboxHeightStatus &&
          !modelCameraOrbitStatus &&
          !modelCameraTargetStatus &&
          !inlineModeSMV &&
          settings.smv.length > 0 &&
          settings.smv[0].type !== 'model' &&
          !panoramaStatus
        "
        class="pre-code"
        v-html="renderDefaultCodeHTML"
      />
      <pre
        v-else-if="viewSMVMode && !inlineModeSMV"
        class="pre-code"
        v-html="renderDefaultCodeHTML"
      />
      <pre
        v-else-if="inlineModeSMV && viewSMVMode"
        class="pre-code"
        v-html="renderInlineModeSMVCodeHTML"
      />
      <pre
        v-else-if="settings.smv.length === 1 && settings.smv[0].type === 'model'"
        class="pre-code"
        v-html="renderModelCodeHTML"
      />
      <pre
        v-else-if="videoThumbnailStatus"
        class="pre-code"
        v-html="renderVideoThumbnailsModeCodeHTML"
      />
      <pre
        v-else-if="
          modelThumbnailStatus ||
          modelSkyboxStatus ||
          modelEnvironmentStatus ||
          modelSkyboxHeightStatus ||
          modelCameraOrbitStatus ||
          modelCameraTargetStatus
        "
        class="pre-code"
        v-html="renderModelWithParamsModeCodeHTML"
      />

      <pre
        v-else-if="spinControlPanelStatus"
        class="pre-code"
        v-html="renderSpinControlModeCodeHTML"
      />

      <pre v-else-if="inlineModeSMV" class="pre-code" v-html="renderInlineModeSMVCodeHTML" />

      <pre v-else-if="panoramaStatus" class="pre-code" v-html="renderPanoramaModeSMVCodeHTML" />

      <pre v-else class="pre-code" v-html="renderDefaultCodeHTML" />
    </div>
  </div>
</template>
<script>
import { modelOptions as options3DParameters } from "@/constants";

import { clearOptions, filterOptionsFromZoom, renderOptionsHTML } from "@/utils/utils";
import { mapGetters, mapState } from "vuex";
import { thumbnailImageHTMLString } from "@/utils/video-utils";
import { withparamsModelHTMLStringModel } from "@/utils/model-utils";
import { zoomHTMLString } from "@/utils/zoom-utils";
import {
  getCodeCSSFromOptions,
  hSpace,
  renderSpinButtonsStyleHTML,
  arrowsCSS,
} from "@/utils/css-utils";
import {
  rT,
  rC,
  rV,
  rO,
  getButtonsClass,
  rButton,
  renderSpinButtonsScriptHTML,
} from "@/utils/render-utils";
import spinControlIconStyle from "@/assets/spin_control_icon_style.json";
import { filterByVideoOptions } from "@/utils/filter-options";
import { prefetchCodeInlineComputed } from "@/utils/prefetch";

export default {
  name: "CodeInline",
  computed: {
    ...mapState([
      "zoomEnable",
      "settings",
      "enableMobileOptions",
      "viewSMVMode",
      "inlineModeSMV/inlineModeSMV",
      "model/modelThumbnailStatus",
      "model/modelSkyboxStatus",
      "model/modelEnvironmentStatus",
      "model/modelSkyboxHeightStatus",
      "model/modelCameraOrbitStatus",
      "model/modelCameraTargetStatus",
      "panorama/panoramaStatus",
    ]),
    ...mapGetters({
      videoThumbnailStatus: "video/videoThumbnailStatus",
      modelThumbnailStatus: "model/modelThumbnailStatus",
      modelSkyboxStatus: "model/modelSkyboxStatus",
      modelEnvironmentStatus: "model/modelEnvironmentStatus",
      modelSkyboxHeightStatus: "model/modelSkyboxHeightStatus",
      modelCameraOrbitStatus: "model/modelCameraOrbitStatus",
      modelCameraTargetStatus: "model/modelCameraTargetStatus",
      inlineModeSMV: "inlineModeSMV/inlineModeSMV",
      scriptByType: "script/scriptByType",
      scriptComponents: "script/components",
      spinControlPanelStatus: "spinControl/spinControlPanelStatus",
      size: "spinControl/size",
      selectedButtons: "spinControl/spinSelectedButtons",
      selectedIconsStyle: "spinControl/spinControlSelectedIconsStyle",
      selectedPosition: "spinControl/spinControlSelectedPosition",
      spinControlBackground: "spinControl/spinControlBackground",
      spinControlIconsColor: "spinControl/spinControlIconsColor",
      panoramaStatus: "panorama/panoramaStatus",
    }),

    videoTitleAndDescription() {
      let [title, description] = filterByVideoOptions(this.settings.options);

      if (title)
        title = ` <span style="color: #606">data-title</span><span style="color: #660;">=</span><span style="color: #080;">"${title.trim()}"</span>`;
      if (description)
        description = ` <span style="color: #606">data-description</span><span style="color: #660;">=</span><span style="color: #080;">"${description.trim()}"</span>`;

      return `${title}${description}`;
    },
    renderArrows() {
      return arrowsCSS();
    },
    modelOptions() {
      let modelOptionsTmp = [];
      let options = this.settings.options
        .split("; ")
        .filter((it) => {
          let [parameter, value] = it.split(":");
          let isOptionOf3DModel = options3DParameters.includes(parameter);
          isOptionOf3DModel && modelOptionsTmp.push(it);
          return !isOptionOf3DModel && it;
        })
        .join("; ");
      options = options.replaceAll("model.", "");

      if (options)
        return ` <span class="atn">data-options</span><span class="pun">=</span><span style="color:#080;">"${options}"</span>`;

      return options;
    },
    containerStyles() {
      const containerStyles = this.settings.optionsCSS.filter(
        (it) => it.type === "container-styles"
      );
      if (containerStyles.length) {
        const stylesString = containerStyles
          .map((it) => `${it.parameter}${it.separator}${it.value}`)
          .join("; ");
        return ` <span class="atn">style</span><span class="pun">=</span><span class="atv">"${stylesString}"</span>`;
      }
      return "";
    },
    prefetchCodeInlineComputed,
    renderDefaultStyles() {
      // console.log(this.settings);
      let arrowsStyles = "";

      if (
        this.settings.optionsCSS.find((e) => e.type !== undefined && e.type.startsWith("arrows"))
      ) {
        arrowsStyles = this.renderArrows;
      }

      let style = arrowsStyles + this.getOptionsCSSCode;

      if (style.length) {
        return this.settings.optionsCSS.length || this.spinControlPanelStatus
          ? `\n<span class="tag">&lt;style&gt;</span>` + style + `\n<span>&lt;/style&gt;</span>`
          : "";
      }

      return "";
    },
    renderSpinControlStyles() {
      let arrowsStyles = "";

      if (
        this.settings.optionsCSS.find((e) => e.type !== undefined && e.type.startsWith("arrows"))
      ) {
        arrowsStyles = this.renderArrows;
      }

      let style = arrowsStyles + this.getSpinControlCSSCode + this.getOptionsCSSCode;

      if (style.length) {
        return `\n<span class="tag">&lt;style&gt;</span>` + style + `\n<span>&lt;/style&gt;</span>`;
      }

      return "";
    },
    videoThumb() {
      const o = !this.settings.options.length
        ? null
        : this.settings.options.split(";").find((e) => e.indexOf("video.thumbnail") >= 0);
      const template = !o
        ? ""
        : `<span class="atn">data-options</span><span class="pun">=</span><span style="color:#080;">"${o
            .trim()
            .replace("video.", "")}"</span> `;
      return template;
    },
    modelThumb() {
      const o = !this.settings.options.length
        ? null
        : this.settings.options.split(";").find((e) => e.indexOf("model.thumbnail") >= 0);
      const template = !o
        ? ""
        : `<span class="atn">data-options</span><span class="pun">=</span><span style="color:#080;">"${o
            .trim()
            .replace("model.", "")}"</span> `;
      return template;
    },
    renderInlineModeSMVItems() {
      return this.settings.smv.map((item) => item.src).join(", ");
    },
    renderDefaultSMVItems() {
      let space = 2;
      if (this.spinControlPanelStatus) {
        space = 4;
      }
      return this.settings.smv
        .map((smvItem) => {
          if (smvItem.type === "video") {
            return `\n${hSpace(space)}<span class="tag">&lt;div</span> ${
              this.videoThumb
            }<span class="atn">data-src</span><span class="pun">=</span><span class="atv">"${
              smvItem.type !== "spins" ? smvItem.src : smvItem.spinsrc
            }"</span>${this.getPined(smvItem.pined)}${this.getZoomHtml(smvItem.type)}${
              this.videoTitleAndDescription
            }<span class="tag">&gt;&lt;/div&gt;</span>`;
          } else if (smvItem.type === "model") {
            return `\n${hSpace(space)}<span class="tag">&lt;div</span> ${
              this.modelThumb
            }<span class="atn">data-src</span><span class="pun">=</span><span class="atv">"${
              smvItem.type !== "spins" ? smvItem.src : smvItem.spinsrc
            }"</span>${this.getPined(smvItem.pined)}${this.getZoomHtml(
              smvItem.type
            )}<span class="tag">&gt;&lt;/div&gt;</span>`;
          } else if (smvItem.type === "panorama") {
            return (
              `\n${hSpace(
                space
              )}<span class="tag">&lt;div</span><span class="atn"> data-swipe-disabled</span><span class="pun">=</span><span class="atv">""</span>${this.getPined(
                smvItem.pined
              )}${this.getZoomHtml(smvItem.type)}<span class="tag">&gt;</span>
      <span class="tag">&lt;iframe</span> <span class="atn">allowfullscreen</span><span class="pun">=</span><span class="atv">""</span> <span class="atn">frameborder</span><span class="pun">=</span><span class="atv">"0"</span> <span class="atn">width</span><span class="pun">=</span><span class="atv">"100%"</span> <span class="atn">height</span><span class="pun">=</span><span class="atv">"100%"</span> <span class="atn">referrerpolicy</span><span class="pun">=</span><span class="atv">"no-referrer-when-downgrade"</span> <span class="atn">src</span><span class="pun">=</span><span class="atv">"https://scripts.sirv.com/pannellum/2.5/embed.html#panorama=` +
              smvItem.src +
              `"</span><span class="tag">&gt;</span><span class="tag">&lt;/iframe&gt;</span>
      <span class="tag">&lt;smv-thumbnail</span> <span class="atn">data-src</span><span class="pun">=</span><span class="atv">"` +
              smvItem.src +
              `"</span><span class="tag">&gt;</span>
      <span class="tag">&lt;/smv-thumbnail&gt;</span>
  <span class="tag">&lt;/div&gt;</span>`
            );
          } else {
            return `\n${hSpace(
              space
            )}<span class="tag">&lt;div</span> <span class="atn">data-src</span><span class="pun">=</span><span class="atv">"${
              smvItem.type !== "spins" ? smvItem.src : smvItem.spinsrc
            }"</span>${this.renderImagesAlts}${this.getPined(smvItem.pined)}${this.getZoomHtml(
              smvItem.type
            )}<span class="tag">&gt;&lt;/div&gt;</span>`;
          }
        })
        .join("");
    },
    renderModelWithParamsModeSMVItems() {
      return this.settings.smv
        .map(
          (smvItem) =>
            `\n <span class="tag">&lt;div</span> <span class="atn">data-src</span><span class="pun">=</span><span class="atv">"${
              smvItem.type !== "spins" && smvItem.type !== "video" ? smvItem.src : smvItem.src
            }"</span>${this.getPined(smvItem.pined)}${this.getZoomHtml(
              smvItem.type
            )}${this.getModelParamsHtml(
              smvItem.type,
              this.settings.options
            )}<span class="tag">&gt;&lt;/div&gt;</span>`
        )
        .join("");
    },
    renderVideoThumbnailsModeSMVItems() {
      return this.settings.smv
        .map(
          (smvItem) =>
            `\n <span class="tag">&lt;div</span> <span class="atn">data-src</span><span class="pun">=</span><span class="atv">"${
              smvItem.type !== "spins" ? smvItem.src : smvItem.spinsrc
            }"</span>${this.getPined(smvItem.pined)}${this.getZoomHtml(
              smvItem.type
            )}${this.getVideoThumbHtml(smvItem.type, this.settings.options)}${
              this.videoTitleAndDescription
            }<span class="tag">&gt;&lt;/div&gt;</span>`
        )
        .join("");
    },
    renderScriptTag() {
      let modules = this.scriptComponents.length ? `?modules=${this.scriptComponents}` : "";
      return `\n<span class="script"><span class="tag">&lt;script</span> <span class="atn">src</span><span class="pun">=</span><span class="atv">"${this.scriptByType}${modules}"</span><span class="tag">&gt;&lt;/script&gt;</span></span>`;
    },

    renderPanoramaModeSMVCodeHTML() {
      return (
        this.prefetchCodeInlineComputed +
        `<span class="tag">&lt;div</span> <span class="atn">class</span><span class="pun">=</span><span class="atv">"Sirv"</span>${this.getOptionsHTML}${this.containerStyles}<span class="tag">&gt;</span>` +
        this.renderDefaultSMVItems +
        `\n<span class="sirv"><span class="tag">&lt;/div&gt;</span></span>` +
        this.renderScriptTag +
        this.renderDefaultStyles
      );
    },
    renderDefaultCodeHTML() {
      return (
        this.prefetchCodeInlineComputed +
        `<span class="tag">&lt;div</span> <span class="atn">class</span><span class="pun">=</span><span class="atv">"Sirv"</span>${this.getOptionsHTML}${this.containerStyles}<span class="tag">&gt;</span>` +
        this.renderDefaultSMVItems +
        `\n<span class="sirv"><span class="tag">&lt;/div&gt;</span></span>` +
        this.renderScriptTag +
        this.renderDefaultStyles
      );
    },
    renderModelCodeHTML() {
      return (
        this.prefetchCodeInlineComputed +
        `<span><span class="tag">&lt;div</span> <span class="atn">class</span><span class="pun">=</span><span class="atv">"Sirv"</span>${this.containerStyles}<span class="tag">&gt;</span></span>` +
        `\n <span><span class="tag">&lt;div</span> <span class="atn">data-src</span><span class="pun">=</span>${this.getModelHTML}${this.modelOptions}<span class="tag">&gt;</span></span><span class="tag">&lt;/div&gt;</span>` +
        `\n<span class="sirv"><span class="tag">&lt;/div&gt;</span></span>` +
        this.renderScriptTag +
        this.renderDefaultStyles
      );
    },
    renderInlineModeSMVCodeHTML() {
      // return this.settings.smv.map((item) => item.src).join(",");
      return (
        this.prefetchCodeInlineComputed +
        `<span class="tag">&lt;div</span> <span class="atn">class</span><span class="pun">=</span><span class="atv">"Sirv"</span> <span class="atn">data-src</span><span class="pun">=</span>"${this.renderInlineModeSMVItems}"${this.getOptionsHTML}${this.containerStyles}<span class="tag">&gt;</span>` +
        `<span class="sirv"><span class="tag">&lt;/div&gt;</span></span>` +
        this.renderScriptTag +
        this.renderDefaultStyles
      );

      // const typeViewRegEx = new RegExp("(\\?thumb)", "ig");
      // return (
      //   this.prefetchCodeInlineComputed +
      //   (this.settings.smv[0].src.match(typeViewRegEx) !== null
      //     ? `<span><span class="tag">&lt;img</span> <span class="atn">class</span><span class="pun">=</span><span class="atv">"Sirv"</span> <span class="atn">data-src</span><span class="pun">=</span>${this.getViewHTML}${this.getOptionsHTML}${this.containerStyles}<span class="tag">&gt;</span></span>`
      //     : `<span><span class="tag">&lt;div</span> <span class="atn">class</span><span class="pun">=</span><span class="atv">"Sirv"</span> <span class="atn">data-src</span><span class="pun">=</span>${
      //         this.getViewHTML
      //       }${this.getOptionsHTML}${this.containerStyles}${this.getPined(
      //         this.settings.smv[0].pined
      //       )}<span class="tag">&gt;</span></span><span class="sirv"><span class="tag">&lt;/div&gt;</span></span>`) +
      //   this.renderScriptTag +
      //   this.renderDefaultStyles
      // );
    },
    renderViewModeCodeHTML() {
      const typeViewRegEx = new RegExp("(\\?thumb)", "ig");
      return (
        this.prefetchCodeInlineComputed +
        (this.settings.smv[1].src.match(typeViewRegEx) !== null
          ? `<span><span class="tag">&lt;img</span> <span class="atn">class</span><span class="pun">=</span><span class="atv">"Sirv"</span> <span class="atn">data-src</span><span class="pun">=</span>${this.getViewHTML}${this.getOptionsHTML}${this.containerStyles}<span class="tag">&gt;</span></span>`
          : `<span><span class="tag">&lt;div</span> <span class="atn">class</span><span class="pun">=</span><span class="atv">"Sirv"</span> <span class="atn">data-src</span><span class="pun">=</span>${
              this.getViewHTML
            }${this.getOptionsHTML}${this.containerStyles}${this.getPined(
              this.settings.smv[1].pined
            )}<span class="tag">&gt;</span></span><span class="sirv"><span class="tag">&lt;/div&gt;</span></span>`) +
        this.renderScriptTag +
        this.renderDefaultStyles
      );
    },
    renderModelWithParamsModeCodeHTML() {
      return (
        this.prefetchCodeInlineComputed +
        `<span><span class="tag">&lt;div</span> <span class="atn">class</span><span class="pun">=</span><span class="atv">"Sirv"</span>${this.getOptionsWithoutModelHTML}${this.containerStyles}<span class="tag">&gt;</span></span>` +
        this.renderModelWithParamsModeSMVItems +
        `\n<span><span class="tag">&lt;/div</span><span class="tag">&gt;</span></span>` +
        this.renderScriptTag +
        this.renderDefaultStyles
      );
    },
    renderVideoThumbnailsModeCodeHTML() {
      return (
        this.prefetchCodeInlineComputed +
        `<span><span class="tag">&lt;div</span> <span class="atn">class</span><span class="pun">=</span><span class="atv">"Sirv"</span>${this.getOptionsWithoutVideoHTML}${this.containerStyles}<span class="tag">&gt;</span></span>` +
        this.renderVideoThumbnailsModeSMVItems +
        `\n<span><span class="tag">&lt;/div</span><span class="tag">&gt;</span></span>` +
        this.renderScriptTag +
        this.renderDefaultStyles
      );
    },
    renderSpinControlModeCodeHTML() {
      return (
        this.prefetchCodeInlineComputed +
        `<span class="tag">&lt;div</span> <span class="atn">class</span><span class="pun">=</span><span class="atv">"viewer"</span><span class="tag">&gt;</span>` +
        `\n${hSpace(
          2
        )}<span class="tag">&lt;div</span> <span class="atn">id</span><span class="pun">=</span><span class="atv">"smv"</span> <span class="atn">class</span><span class="pun">=</span><span class="atv">"Sirv"</span>${
          this.getOptionsHTML
        }${this.containerStyles}<span class="tag">&gt;</span>` +
        this.renderDefaultSMVItems +
        `\n${hSpace(2)}<span class="sirv"><span class="tag">&lt;/div&gt;</span></span>` +
        `\n${hSpace(
          2
        )}<span class="tag">&lt;div</span> <span class="atn">class</span><span class="pun">=</span><span class="atv">"spin-buttons"</span><span class="tag">&gt;</span>` +
        this.renderButtons +
        this.renderInfoBlock +
        `\n${hSpace(2)}<span class="sirv"><span class="tag">&lt;/div&gt;</span></span>` +
        `\n<span class="sirv"><span class="tag">&lt;/div&gt;</span></span>` +
        this.renderScriptTag +
        this.renderSpinButtonsScriptHTML() +
        this.renderSpinControlStyles
      );
    },
    renderInfoBlock() {
      return `\n${hSpace(
        4
      )}<span class="tag">&lt;div</span> <span class="atn">id</span><span class="pun">=</span><span class="atv">"info"</span> <span class="atn">style</span><span class="pun">=</span><span class="atv">"display: none;"</span><span class="tag">&gt;</span><span class="sirv"><span class="tag">&lt;/div&gt;</span></span>`;
    },
    getSpinControlCSSCode() {
      const icon = spinControlIconStyle.find((e) => e.value == this.selectedIconsStyle);
      return (
        `\n${hSpace(2)}<span class="atn">@import url(</span><span class="atv">${
          icon.url
        }</span><span class="atn">)</span>;\n` +
        renderSpinButtonsStyleHTML(
          this.selectedPosition,
          this.selectedIconsStyle,
          this.spinControlBackground,
          this.spinControlIconsColor,
          this.size
        )
      );
    },
    getOptionsCSSCode() {
      return getCodeCSSFromOptions(this.settings.optionsCSS, true);
    },
    getViewHTML() {
      return `"${this.settings.smv[0].src}"`;
    },
    getModelHTML() {
      return `"${this.settings.smv[0].src}"`;
    },
    renderButtons() {
      return this.selectedButtons.map((b) => this.renderButtonWithStyle(b)).join("");
    },
    renderImagesAlts() {
      const alts = this.settings.options.split("; ").find((it) => it.includes("image.alt:"));
      if (alts && alts.includes(":Manual|")) {
        let alt = alts.split("|")[1].replace(";", "");

        if (alt.length) {
          const parts = [
            ' <span class="atn">data-alt</span><span class="pun">=</span><span class="atv">"',
            `${alt}`,
            '"</span>',
          ];

          return parts.join("");
        }
      }

      return "";
    },
    getOptionsHTML() {
      const optionsRaw = clearOptions(this.settings.options).filter((e) => {
        return (
          e.indexOf("video.thumbnail") === -1 &&
          e.indexOf("image.alt") === -1 &&
          e.indexOf("single.image.fadein") === -1 &&
          e.indexOf("video.title") === -1 &&
          e.indexOf("video.description") === -1
        );
      });
      const mobileOptionRaw = clearOptions(this.settings.optionsMobile).filter((e) => {
        return (
          e.indexOf("image.alt") === -1 &&
          e.indexOf("single.image.fadein") === -1 &&
          e.indexOf("video.title") === -1 &&
          e.indexOf("video.description") === -1
        );
      });
      if (this.viewSMVMode && !this.zoomEnable) {
        optionsRaw.push("zoom.ratio:0");
      }
      let options = filterOptionsFromZoom(optionsRaw);
      let mobileOptions = filterOptionsFromZoom(mobileOptionRaw);

      const dataOptions = "data-options";
      const dataMobileOptions = "data-mobile-options";
      const optionsHTML = renderOptionsHTML(dataOptions, options);
      const mobileOptionsHTML = renderOptionsHTML(dataMobileOptions, mobileOptions);
      const separator = options !== "" && mobileOptions !== "" ? " " : "";
      return `${optionsHTML}${separator}${mobileOptionsHTML}`;
    },
    getOptionsWithoutVideoHTML() {
      const optionsRaw = clearOptions(this.settings.options).filter((e) => {
        return (
          e.indexOf("image.alt") === -1 &&
          e.indexOf("single.image.fadein") === -1 &&
          e.indexOf("video.title") === -1 &&
          e.indexOf("video.description") === -1
        );
      });
      const mobileOptionRaw = clearOptions(this.settings.optionsMobile).filter((e) => {
        return (
          e.indexOf("image.alt") === -1 &&
          e.indexOf("single.image.fadein") === -1 &&
          e.indexOf("video.title") === -1 &&
          e.indexOf("video.description") === -1
        );
      });
      const optionsRawWithoutVideoThumbnail = optionsRaw.filter(
        (e) => e.indexOf("video.thumbnail") === -1
      );
      let options = filterOptionsFromZoom(optionsRawWithoutVideoThumbnail);
      let mobileOptions = filterOptionsFromZoom(mobileOptionRaw);

      const dataOptions = "data-options";
      const dataMobileOptions = "data-mobile-options";
      const optionsHTML = renderOptionsHTML(dataOptions, options);
      const mobileOptionsHTML = renderOptionsHTML(dataMobileOptions, mobileOptions);
      const separator = options !== "" && mobileOptions !== "" ? " " : "";

      return `${optionsHTML}${separator}${mobileOptionsHTML}`;
    },
    getOptionsWithoutModelHTML() {
      const optionsRaw = clearOptions(this.settings.options).filter((e) => {
        return e.indexOf("image.alt") === -1 && e.indexOf("single.image.fadein") === -1;
      });
      const mobileOptionRaw = clearOptions(this.settings.optionsMobile).filter((e) => {
        return e.indexOf("image.alt") === -1 && e.indexOf("single.image.fadein") === -1;
      });
      const optionsRawWithoutModelThumbnail = optionsRaw
        .filter((e) => e.indexOf("model.thumbnail") === -1)
        .filter((e) => e.indexOf("skybox.image") === -1)
        .filter((e) => e.indexOf("skybox.height") === -1)
        .filter((e) => e.indexOf("camera.orbit") === -1)
        .filter((e) => e.indexOf("camera.target") === -1)
        .filter((e) => e.indexOf("environmentImage") === -1);

      let options = filterOptionsFromZoom(optionsRawWithoutModelThumbnail);
      let mobileOptions = filterOptionsFromZoom(mobileOptionRaw);

      const dataOptions = "data-options";
      const dataMobileOptions = "data-mobile-options";
      const optionsHTML = renderOptionsHTML(dataOptions, options);
      const mobileOptionsHTML = renderOptionsHTML(dataMobileOptions, mobileOptions);
      const separator = options !== "" && mobileOptions !== "" ? " " : "";

      return `${optionsHTML}${separator}${mobileOptionsHTML}`;
    },
  },
  methods: {
    renderSpinButtonsScriptHTML() {
      return renderSpinButtonsScriptHTML(this.selectedButtons);
    },
    renderButtonWithStyle(button) {
      const space = 4;
      if (button === "play") {
        return (
          `\n${hSpace(space)}${rButton(this.selectedIconsStyle, button)}` +
          `\n${hSpace(space)}${rButton(this.selectedIconsStyle, "pause")}`
        );
      }
      return `\n${hSpace(space)}${rButton(this.selectedIconsStyle, button)}`;
    },
    getVideoThumbHtml(type, options) {
      const formattedOptions = options
        .split(";")
        .filter((e) => e !== "" && e.indexOf("video.thumbnail") !== -1);
      const containElementIndex = formattedOptions.length && formattedOptions[0].indexOf(":");
      const dataThumbnailImage =
        containElementIndex !== -1 ? formattedOptions[0].substring(containElementIndex + 1) : "";
      return thumbnailImageHTMLString(type, dataThumbnailImage);
    },
    getModelParamsHtml(type, options) {
      const formattedOptions = options
        .split(";")
        .filter(
          (e) =>
            (e !== "" && e.indexOf("model.thumbnail") !== -1 && e.indexOf("thumbnail") !== -1) ||
            e.indexOf("skybox.image") !== -1 ||
            e.indexOf("skybox.height") !== -1 ||
            e.indexOf("environmentImage") !== -1 ||
            e.indexOf("camera.orbit") !== -1 ||
            e.indexOf("camera.target") !== -1
        );

      // const containElementIndex = formattedOptions.length && formattedOptions[0].indexOf(":");
      // const dataWithParams =
      //   containElementIndex !== -1 ? formattedOptions[0].substring(containElementIndex + 1) : "";
      return withparamsModelHTMLStringModel(
        type,
        formattedOptions.toString().replaceAll("model.", "")
      );
    },
    getZoomHtml(type) {
      if (type === "spins" && this.zoomEnable) return "";
      if (type === "video" && this.zoomEnable) return "";
      if (type === "model" && this.zoomEnable) return "";
      if (type === "panorama") return "";
      return zoomHTMLString(this.zoomEnable);
    },
    getPined(pin) {
      return pin
        ? ' <span style="color: #606;">data-pinned</span><span style="color: #660;">=</span>' +
            `<span style="color:#080;">"${pin}"</span>`
        : "";
    },
  },
};
</script>
<style lang="scss" scoped>
.codewrap {
  position: relative;
  padding: 10px 10px 0;
  border-radius: 4px;
  box-shadow: 0 8px 40px rgba(50, 123, 186, 0.16), 0 4px 4px rgba(50, 123, 186, 0);
  color: #008;
  overflow-x: auto;
  &::-webkit-scrollbar {
    width: 0.2em;
    height: 0.2em;
  }
}
.code {
  display: block;
  //position: relative;
  //width: 100%;
  overflow-x: hidden;
  overflow-y: hidden;
  padding: 10px 0 0;
  width: min-content;
}
.line {
  display: block;
}
.copybutton {
  position: absolute;
  right: 10px;
  top: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  justify-content: center;
  align-items: center;
  padding: 0;
  background-color: transparent;
  border: none;
  color: #327bba;
  &:hover {
    opacity: 0.8;
  }
}
.copymessage {
  position: absolute;
  top: 5px;
  right: 55px;
}
.pre-code {
  margin: 0;
  padding: 10px 0;
  line-height: 1.25;
  font-size: 14px;
  overflow-y: hidden;
  overflow-x: hidden;
  &::-webkit-scrollbar {
    width: 0.2em;
    height: 0.2em;
  }

  &::-webkit-scrollbar-track {
    box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  }

  &::-webkit-scrollbar-thumb {
    background-color: #327bba;
    outline: 1px solid slategrey;
  }
}
.sirv {
  display: inline-block;
}
.script {
  display: inline-block;
  margin-top: 10px;
}
.tag,
.zoom .tag {
  color: #008;
}
.atn,
.zoom .atn {
  color: #606;
}
.pun {
  color: #660;
}
.atv {
  color: #080;
}
</style>
